import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import { HomeworkIdeaCardProps } from "./HomeworkIdeaCard";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import {
  HomeworkIdeaResponseDto,
  HomeworkIdeaItem,
} from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  ideaContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f0fdf4",
    borderRadius: 8,
  },
  ideaTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#16a34a",
    marginBottom: 8,
  },
  complexityBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#ecfccb",
    color: "#3f6212",
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    marginBottom: 8,
  },
  description: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.4,
    marginBottom: 8,
  },
  resourceItem: {
    flexDirection: "row",
    marginBottom: 4,
    paddingLeft: 8,
  },
  bullet: {
    width: 12,
    fontSize: 11,
    color: "#16a34a",
  },
});

const HomeworkIdeaDocument = ({
  homeworkIdea,
  customizationSettings,
}: {
  homeworkIdea: HomeworkIdeaResponseDto & { reportIdeas?: HomeworkIdeaItem[] };
  customizationSettings: PdfCustomizationSettings;
}) => {
  const { t } = useTranslation();

  // Handle backward compatibility - use reportIdeas if available
  const ideas = homeworkIdea.reportIdeas || [];

  return (
    <PdfLayout
      courseName={homeworkIdea.courseName || ""}
      gradeLevel={homeworkIdea.gradeLevel || 0}
      topic={homeworkIdea.title || homeworkIdea.prompt || ""}
      imageUrl={homeworkIdea.imageUrl}
      author={homeworkIdea.authorName}
      createdDate={homeworkIdea.createdAt}
      headerTextColor="#16a34a"
      customizationSettings={customizationSettings}
    >
      {ideas.map((idea, index) => (
        <View key={index} style={styles.ideaContainer} wrap={false}>
          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.ideaTitle,
                  fontSize: scaleFontSize(
                    styles.ideaTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
            content={idea.title || ""}
          />
          <Text
            style={[
              styles.complexityBadge,
              {
                fontSize: scaleFontSize(
                  styles.complexityBadge.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {t(`homeworkIdeas.complexity.${idea.complexity_level}`)}
          </Text>

          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.description,
                  fontSize: scaleFontSize(
                    styles.description.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
            content={idea.description || ""}
          />

          {idea.outline && (
            <>
              <MarkdownRenderer
                customStyle={{
                  text: {
                    text: {
                      ...commonStyles.sectionTitle,
                      fontSize: scaleFontSize(
                        commonStyles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      ...commonStyles.sectionTitle,
                      fontSize: scaleFontSize(
                        commonStyles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  },
                }}
                content={t("homeworkIdeas.outline")}
              />
              <MarkdownRenderer
                customStyle={{
                  text: {
                    text: {
                      ...commonStyles.sectionContent,
                      fontSize: scaleFontSize(
                        commonStyles.sectionContent.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      ...commonStyles.sectionContent,
                      fontSize: scaleFontSize(
                        commonStyles.sectionContent.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  },
                }}
                content={idea.outline}
              />
            </>
          )}

          {idea.recommended_resources && (
            <>
              <Text style={commonStyles.sectionTitle}>
                {t("homeworkIdeas.resources")}
              </Text>
              {idea.recommended_resources.map((resource, idx) => (
                <View key={idx} style={styles.resourceItem}>
                  <Text style={styles.bullet}>•</Text>
                  <MarkdownRenderer
                    customStyle={{
                      text: {
                        text: {
                          fontSize: scaleFontSize(
                            9,
                            customizationSettings?.fontSize,
                          ),
                        },
                      },
                    }}
                    content={resource}
                  />
                </View>
              ))}
            </>
          )}
        </View>
      ))}
    </PdfLayout>
  );
};

function HomeworkIdeaCardPDF(
  props: Omit<HomeworkIdeaCardProps, "onCreateAgain">,
) {
  return <HomeworkIdeaDocument {...props} />;
}

export default HomeworkIdeaCardPDF;
